import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { DataTableColumnHeader, ActionsCell } from "@/components/data-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { format } from "date-fns";
import type { Organization } from "@/types/organization";

type EditFn = (organization: Organization) => void;
type DeleteFn = (organization: Organization) => void;

export function getOrganizationColumns(
  i18n: I18n,
  onEdit: EditFn,
  onDelete: DeleteFn,
): ColumnDef<Organization, unknown>[] {
  return [
    {
      accessorKey: "id",
      meta: { label: i18n._(msg`ID`) },
      header: i18n._(msg`ID`),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs text-(--color-text-secondary)">
          {getValue() as number}
        </span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Name`)} />
      ),
      cell: ({ getValue }) => (
        <span className="font-medium">{getValue() as string}</span>
      ),
    },
    {
      accessorKey: "slug",
      meta: { label: i18n._(msg`Slug`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Slug`)} />
      ),
      cell: ({ getValue }) => (
        <span className="font-mono text-xs text-(--color-text-secondary)">
          {getValue() as string}
        </span>
      ),
    },
    {
      accessorKey: "isActive",
      meta: { label: i18n._(msg`Status`) },
      header: i18n._(msg`Status`),
      cell: ({ getValue }) => (
        <Badge variant={getValue() ? "success" : "secondary"}>
          {getValue() ? i18n._(msg`Active`) : i18n._(msg`Inactive`)}
        </Badge>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "createdAt",
      meta: { label: i18n._(msg`Created`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Created`)} />
      ),
      cell: ({ getValue }) =>
        format(new Date(getValue() as string), "MMM d, yyyy"),
    },
    {
      accessorKey: "updatedAt",
      meta: { label: i18n._(msg`Updated`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Updated`)} />
      ),
      cell: ({ getValue }) =>
        format(new Date(getValue() as string), "MMM d, yyyy"),
    },
    {
      id: "companies",
      meta: { label: i18n._(msg`Concepts`), defaultVisible: false },
      header: i18n._(msg`Concepts`),
      cell: ({ row }) => row.original._count?.companies ?? 0,
      enableSorting: false,
    },
    {
      id: "regions",
      meta: { label: i18n._(msg`Regions`), defaultVisible: false },
      header: i18n._(msg`Regions`),
      cell: ({ row }) => row.original._count?.regions ?? 0,
      enableSorting: false,
    },
    {
      id: "users",
      meta: { label: i18n._(msg`Users`), defaultVisible: false },
      header: i18n._(msg`Users`),
      cell: ({ row }) => row.original._count?.users ?? 0,
      enableSorting: false,
    },
    {
      id: "actions",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <ActionsCell
          items={[
            {
              label: i18n._(msg`Edit`),
              onClick: () => onEdit(row.original),
            },
            {
              label: i18n._(msg`Delete`),
              onClick: () => onDelete(row.original),
              destructive: true,
            },
          ]}
        />
      ),
    },
  ];
}
